import React from 'react';
import { motion } from 'framer-motion';
import { NewsStoryProps } from './NewsStory';
import { MotionImage, useSanitizedTranslation } from 'ui';

export type SingleNewsLayoutProps = {
  story: NewsStoryProps;
  accent: string;
};

const imageVariants = {
  initial: {
    scale: 1,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
  hover: {
    scale: 1.04,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
};

export const SingleNewsLayout = ({ story, accent }: SingleNewsLayoutProps) => {
  const { t } = useSanitizedTranslation();

  if (!story) return null;

  const url = story.link ? story.link : (story.slug || '').replace(/^\/?/, '/');
  const linkOffsite = url.startsWith('/press') ? false : true;
  const storyDate = story.date
    ? new Date(story.date).toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-8" style={{ backgroundColor: '#f8f8f8' }}>
      <div className="wrapper">
        <motion.a
          initial="initial"
          animate="initial"
          whileHover="hover"
          href={url}
          target={linkOffsite ? '_blank' : '_self'}
          className="group flex min-h-[300px] w-full cursor-pointer flex-col items-start lg:flex-row"
        >
          <motion.div className="relative flex aspect-video w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-md lg:w-2/3">
            <MotionImage
              src={story.thumbnail.src}
              alt={story.thumbnail.alt ?? ''}
              fill
              objectFit="cover"
              variants={imageVariants}
              sizes="(max-width: 1024px) 100vw, 66vw"
            />
          </motion.div>
          <div className="flex flex-1 flex-col justify-center py-8 md:p-8 lg:w-1/3">
            <h3 className="type-subheading-xs  pb-2 text-left text-black" data-i18n={story.title} dangerouslySetInnerHTML={{ __html: t(story.title) }} />
            <div
              className="text-body-base mb-2 line-clamp-3 w-full overflow-hidden text-left text-[#60545E]"
              data-i18n={story.description}
              dangerouslySetInnerHTML={{ __html: t(story.description) }}
            />
            <p className="text-body-base mb-6 mt-auto pt-4 text-left text-xumoBlack opacity-60">{storyDate}</p>
            <span
              className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide group-hover:text-xumoBerry"
              style={{ color: accent || '#00C9B7' }}
            >
              Read More
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="10" viewBox="0 0 15 10" fill="none">
                <path
                  d="M0 5.52H12.12L9.19 8.45L10.24 9.5L15 4.74L10.25 0L9.19 1.05L12.17 4.03H0V5.52Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </motion.a>
      </div>
    </div>
  );
};
